import React from "react";
import { Search, SlidersHorizontal, ChevronDown, ChevronUp, FileText, X, ArrowRight, Wrench } from "lucide-react";

export const SUPPLIER_TYPES = ["Manufacturer", "Wholesaler", "Distributor", "Retailer"];

const inputCls = "w-full px-3 py-2 text-sm border border-[#C4CFC9] bg-white rounded-sm focus:border-[#264839] focus:outline-none";

/** Bold full-width RFQ banner shown above the filter bar (category + search pages). */
export const RfqStrip = ({ categoryName, onClick, disabled, testPrefix = "category", headline, className = "mb-5", note }) => (
  <div className={`${className} bg-[#264839] text-white px-5 sm:px-8 py-5 sm:py-6 flex flex-col sm:flex-row sm:items-center gap-4 sm:gap-6 shadow-lg shadow-[#264839]/20`} data-testid={`${testPrefix}-rfq-banner`}>
    <div className="flex items-start gap-4 flex-1 min-w-0">
      <div className="w-11 h-11 shrink-0 bg-white/10 flex items-center justify-center">
        <FileText className="w-5 h-5 text-[#F0C866]" />
      </div>
      <div className="min-w-0">
        <div className="text-[10px] uppercase tracking-[0.25em] text-[#F0C866] mb-1">Not sure who fits?</div>
        <div className="font-serif text-xl sm:text-2xl leading-tight" data-testid={`${testPrefix}-rfq-headline`}>{headline || `Send one RFQ to up to 4 top-ranked ${categoryName} suppliers`}</div>
        <div className="text-sm text-white/75 mt-1">One form, multiple quotes — free for buyers, always.</div>
        {note && <div className="text-xs text-[#F0C866]/90 mt-1.5" data-testid={`${testPrefix}-rfq-note`}>{note}</div>}
      </div>
    </div>
    <button type="button" onClick={onClick} disabled={disabled} className="shrink-0 inline-flex items-center justify-center gap-2 bg-white text-[#264839] px-7 py-3.5 text-base font-semibold tracking-tight hover:bg-[#F0EFEA] transition-colors disabled:opacity-40 disabled:cursor-not-allowed whitespace-nowrap" data-testid={`${testPrefix}-rfq-cta`}>
      Request quotes <ArrowRight className="w-5 h-5" />
    </button>
  </div>
);

/**
 * Compact horizontal filter bar: search · country · supplier-type checkboxes · Advanced toggle · Clear.
 * `children` is the Advanced filters content, rendered only while `advancedOpen`.
 */
export const SupplierFilterPanel = ({
  q, onQ, onSubmit, country, onCountry, countryOptions = [], supplierTypes = [], onSupplierTypes,
  customOem = false, onCustomOem,
  onClear, anyActive, advancedCount = 0, advancedOpen = false, onToggleAdvanced, children,
  activeChips = [], sticky = true,
  searchPlaceholder = "Search products or suppliers…", testPrefix = "supplier-filter",
}) => {
  const active = anyActive ?? !!(q || country || supplierTypes.length || customOem);
  // Pinned below the 64px site header while scrolling; un-pinned while the tall Advanced panel is open.
  const pin = sticky && !advancedOpen ? "sticky top-16 z-30 shadow-[0_6px_16px_-12px_rgba(17,33,26,0.35)]" : "";
  return (
    <section className={`mb-4 border border-[#E8ECE9] bg-white transition-shadow ${pin}`} data-testid={`${testPrefix}-panel`}>
      <div className="px-4 py-3 flex flex-wrap items-center gap-x-4 gap-y-2" data-testid={`${testPrefix}-bar`}>
        <form role="search" onSubmit={(e) => { e.preventDefault(); onSubmit?.(q); }} className="flex items-stretch flex-1 min-w-[220px] basis-full sm:basis-auto" data-testid={`${testPrefix}-search-form`}>
          <div className="relative flex-1">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-[#7D8C85]" />
            <input value={q} onChange={(e) => onQ(e.target.value)} className={`${inputCls} pl-9 ${onSubmit ? "rounded-r-none border-r-0" : ""}`} placeholder={searchPlaceholder} aria-label="Search suppliers" enterKeyHint="search" data-testid={`${testPrefix}-q`} />
          </div>
          {onSubmit && (
            <button type="submit" className="shrink-0 inline-flex items-center gap-1.5 px-4 text-sm font-semibold bg-[#264839] text-white hover:bg-[#1c3529] transition-colors" aria-label="Search" data-testid={`${testPrefix}-search-btn`}>
              <Search className="w-4 h-4" /> Search
            </button>
          )}
        </form>
        <select value={country} onChange={(e) => onCountry(e.target.value)} className={`${inputCls} w-auto min-w-[160px] max-w-[220px]`} aria-label="Country" data-testid={`${testPrefix}-country`}>
          <option value="">All countries</option>
          {countryOptions.map((c) => <option key={c.value} value={c.value}>{c.label}</option>)}
        </select>
        <div className="flex flex-wrap items-center gap-x-3 gap-y-1" data-testid={`${testPrefix}-type`}>
          {SUPPLIER_TYPES.map((t) => {
            const on = supplierTypes.includes(t);
            return (
              <label key={t} className="inline-flex items-center gap-1.5 text-sm text-[#4A5550] cursor-pointer whitespace-nowrap">
                <input type="checkbox" checked={on} onChange={() => onSupplierTypes(on ? supplierTypes.filter((x) => x !== t) : [...supplierTypes, t])} className="accent-[#264839]" data-testid={`${testPrefix}-type-${t.toLowerCase()}`} />
                {t}
              </label>
            );
          })}
          {onCustomOem && (
            <label className={`inline-flex items-center gap-1.5 text-sm cursor-pointer whitespace-nowrap pl-3 border-l border-[#E8ECE9] ${customOem ? "text-[#264839] font-semibold" : "text-[#4A5550]"}`} title="Only suppliers offering custom-made or OEM / private-label products">
              <input type="checkbox" checked={customOem} onChange={(e) => onCustomOem(e.target.checked)} className="accent-[#264839]" data-testid={`${testPrefix}-custom-oem`} />
              <Wrench className="w-3.5 h-3.5 text-[#B35C4A]" /> Custom / OEM
            </label>
          )}
        </div>
        <div className="flex items-center gap-3 ml-auto">
          {onToggleAdvanced && (
            <button type="button" onClick={onToggleAdvanced} aria-expanded={advancedOpen} className={`inline-flex items-center gap-1.5 text-xs font-medium px-3 py-1.5 border transition ${advancedOpen ? "bg-[#264839] text-white border-[#264839]" : "text-[#264839] border-[#264839]/50 hover:border-[#264839]"}`} data-testid={`${testPrefix}-advanced-toggle`}>
              <SlidersHorizontal className="w-3.5 h-3.5" /> Advanced filters
              {advancedCount > 0 && <span className={`text-[10px] px-1.5 rounded-full ${advancedOpen ? "bg-white text-[#264839]" : "bg-[#264839] text-white"}`} data-testid={`${testPrefix}-advanced-count`}>{advancedCount}</span>}
              {advancedOpen ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}
            </button>
          )}
          {onClear && (
            <button type="button" onClick={onClear} disabled={!active} className="text-xs text-[#B35C4A] hover:underline disabled:opacity-40 disabled:no-underline" data-testid={`${testPrefix}-clear`}>
              Clear
            </button>
          )}
        </div>
      </div>
      {activeChips.length > 0 && (
        <div className="px-4 pb-3 -mt-1 flex flex-wrap items-center gap-1.5" data-testid={`${testPrefix}-active-chips`}>
          <span className="text-[10px] uppercase tracking-widest text-[#7D8C85] mr-1">Active</span>
          {activeChips.map((c) => (
            <button key={c.key} type="button" onClick={c.onRemove} className="inline-flex items-center gap-1 text-[11px] pl-2.5 pr-1.5 py-1 bg-[#264839] text-white border border-[#264839] hover:bg-[#1c3529] transition" aria-label={`Remove filter ${c.label}`} data-testid={`${testPrefix}-chip-${c.key}`}>
              {c.group && <span className="text-white/60">{c.group}:</span>}
              {c.label}
              <X className="w-3 h-3 ml-0.5 text-white/80" />
            </button>
          ))}
          {onClear && (
            <button type="button" onClick={onClear} className="text-[11px] text-[#B35C4A] hover:underline ml-1" data-testid={`${testPrefix}-chips-clear`}>Clear all</button>
          )}
        </div>
      )}
      {advancedOpen && children && (
        <div className="border-t border-[#E8ECE9] px-4 py-4 bg-[#FAFAF7]" data-testid={`${testPrefix}-advanced`}>
          {children}
        </div>
      )}
    </section>
  );
};

export default SupplierFilterPanel;
